import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Plus, Edit2, Save, X, Trash2, AlertTriangle, Copy, CheckCircle2, UserMinus, UserX } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { AddStudentPanelV2 } from '../components/AddStudentPanelV2';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { DjayAvatar } from '../../../mascot';
import { type Classroom, type TeacherAccount, type ClassroomValidationWarning, generateClassroomId, validateNewClassroom, findDuplicateClassroomName, isLearnerAccount } from '../../../accounts';
import { StudentData } from './StudentDetailPanel';
interface ManagementPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  students: StudentData[];
}
export function ManagementPage({
  activeAccount,
  classrooms = [],
  selectedClassroomId = null,
  students
}: ManagementPageProps) {
  const {
    accounts,
    addClassroom,
    updateClassroom,
    removeClassroom,
    removeLearnerFromClassroom,
    unlinkTeacherFromLearner
  } = useAppStore();
  const [isEditingClass, setIsEditingClass] = useState<string | null>(null);
  const [editClassName, setEditClassName] = useState('');
  const [editGroupLevel, setEditGroupLevel] = useState(6);
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [newGroupLevel, setNewGroupLevel] = useState(6);
  const [classWarnings, setClassWarnings] = useState<ClassroomValidationWarning[]>([]);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [deleteClassroomId, setDeleteClassroomId] = useState<string | null>(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [newClassroomId, setNewClassroomId] = useState<string | null>(null);
  const [pendingRemoveStudent, setPendingRemoveStudent] = useState<{
    studentId: string;
    studentName: string;
    classroomId: string;
    classroomName: string;
  } | null>(null);
  const [pendingUnlinkStudent, setPendingUnlinkStudent] = useState<{
    studentId: string;
    studentName: string;
  } | null>(null);
  const handleSaveClass = (classroomId: string) => {
    if (editClassName.trim()) {
      const duplicates = findDuplicateClassroomName(accounts, editClassName.trim(), classroomId);
      if (duplicates.length > 0) {
        const dup = duplicates[0];
        const isSameTeacher = dup.classroom.teacherId === activeAccount.id;
        setClassWarnings([{
          type: 'duplicate_name',
          message: isSameTeacher ? `Je hebt al een klas met de naam "${dup.classroom.name}".` : `"${dup.classroom.name}" bestaat al bij ${dup.teacherName}.`,
          details: {
            classroomName: dup.classroom.name,
            classroomId: dup.classroom.id,
            teacherName: dup.teacherName,
            teacherId: dup.classroom.teacherId
          }
        }]);
        return;
      }
      updateClassroom(activeAccount.id, classroomId, {
        name: editClassName.trim(),
        groupLevel: editGroupLevel
      });
      setIsEditingClass(null);
      setClassWarnings([]);
    }
  };
  const handleAddClassroom = () => {
    if (!newClassName.trim()) return;
    const warnings = validateNewClassroom(accounts, newClassName.trim(), activeAccount.id);
    const hasSameTeacherDuplicate = warnings.some(w => w.details.teacherId === activeAccount.id);
    if (hasSameTeacherDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    if (warnings.length > 0 && !confirmDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    const newClassroom: Classroom = {
      id: generateClassroomId(),
      name: newClassName.trim(),
      groupLevel: newGroupLevel,
      learnerIds: [],
      createdAt: Date.now(),
      teacherId: activeAccount.id,
      joinCode: '' // Auto-generated by addClassroom()
    };
    addClassroom(activeAccount.id, newClassroom);
    setIsAddingClass(false);
    setNewClassName('');
    setNewGroupLevel(6);
    setClassWarnings([]);
    setConfirmDuplicate(false);
    setNewClassroomId(newClassroom.id);
    setTimeout(() => setNewClassroomId(null), 3000);
  };
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };
  const activeClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const classStudents = (activeClassroom ? students.filter(s => activeClassroom.learnerIds.includes(s.id)) : students).sort((a, b) => a.name.localeCompare(b.name));
  return <PageLayout title="Klas Beheer" description="Beheer je klassen en leerlingen" data-id="element-5951">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-5952">
        {/* Classrooms Section */}
        <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-5953">
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-5954">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-5955">
              Mijn Klassen
            </h3>
            <Button onClick={() => setIsAddingClass(true)} variant="primary" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-5956">
              <Plus className="w-4 h-4" data-id="element-5957" />
              Nieuwe Klas
            </Button>
          </div>
          <div className="p-4 space-y-4" data-id="element-5958">
            {isAddingClass && <div className="p-5 bg-indigo-50/50 border border-indigo-100 rounded-xl" data-id="element-5959">
                <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-5960">
                  Nieuwe klas toevoegen
                </h4>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-5961">
                  <div className="flex-1 w-full" data-id="element-5962">
                    <Input variant="admin" label="Klasnaam" value={newClassName} onChange={e => setNewClassName(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddClassroom();
                  }
                }} placeholder="Bijv. Groep 7B" autoFocus data-id="element-5963" />
                  </div>
                  <div className="w-full sm:w-32" data-id="element-5964">
                    <Select variant="admin" label="Niveau" value={newGroupLevel} onChange={e => setNewGroupLevel(parseInt(e.target.value))} options={[{
                  value: '6',
                  label: 'Groep 6'
                }, {
                  value: '7',
                  label: 'Groep 7'
                }, {
                  value: '8',
                  label: 'Groep 8'
                }]} data-id="element-5965" />
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-5" data-id="element-5966">
                    <Button onClick={handleAddClassroom} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-5967">
                      <Save className="w-4 h-4" data-id="element-5968" />
                      Aanmaken
                    </Button>
                    <Button onClick={() => {
                  setIsAddingClass(false);
                  setClassWarnings([]);
                  setConfirmDuplicate(false);
                }} variant="secondary" data-id="element-5969">
                      Annuleren
                    </Button>
                  </div>
                </div>
                {classWarnings.length > 0 && <div className="mt-3 space-y-2" data-id="element-5970">
                    {classWarnings.map((w, i) => <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm ${w.details.teacherId === activeAccount.id ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`} data-id="element-5971">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5972" />
                        <div data-id="element-5973">
                          <p className="font-medium" data-id="element-5974">{w.message}</p>
                          {w.details.teacherId !== activeAccount.id && !confirmDuplicate && <button onClick={() => setConfirmDuplicate(true)} className="mt-1 text-xs font-bold underline hover:no-underline" data-id="element-5975">
                                Ja, toch aanmaken
                              </button>}
                        </div>
                      </div>)}
                  </div>}
              </div>}

            {classrooms.length === 0 && !isAddingClass ? <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-5976">
                <div className="mb-4 flex justify-center" data-id="element-5977">
                  <DjayAvatar size="md" emotion="thinking" data-id="element-5978" />
                </div>
                <p className="text-slate-500 mb-4 font-medium" data-id="element-5979">
                  Je hebt nog geen klassen aangemaakt.
                </p>
                <Button onClick={() => setIsAddingClass(true)} variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-5980">
                  Maak je eerste klas aan
                </Button>
              </div> : <div className="grid grid-cols-1 gap-3" data-id="element-5981">
                {classrooms.map((classroom: Classroom) => <motion.div key={classroom.id} initial={newClassroomId === classroom.id ? {
              backgroundColor: '#f0fdf4',
              borderColor: '#86efac'
            } : {}} animate={{
              backgroundColor: '#ffffff',
              borderColor: '#e2e8f0'
            }} transition={{
              duration: 2,
              delay: 1
            }} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors shadow-sm" data-id="element-5982">
                    {isEditingClass === classroom.id ? <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full" data-id="element-5983">
                        <div className="flex-1 w-full" data-id="element-5984">
                          <Input variant="admin" value={editClassName} onChange={e => setEditClassName(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveClass(classroom.id);
                    }
                  }} placeholder="Klasnaam" autoFocus data-id="element-5985" />
                        </div>
                        <div className="w-full sm:w-32" data-id="element-5986">
                          <Select variant="admin" value={editGroupLevel} onChange={e => setEditGroupLevel(parseInt(e.target.value))} options={[{
                    value: '6',
                    label: 'Groep 6'
                  }, {
                    value: '7',
                    label: 'Groep 7'
                  }, {
                    value: '8',
                    label: 'Groep 8'
                  }]} data-id="element-5987" />
                        </div>
                        <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-0" data-id="element-5988">
                          <Button onClick={() => handleSaveClass(classroom.id)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-5989">
                            <Save className="w-4 h-4" data-id="element-5990" />
                            Opslaan
                          </Button>
                          <Button onClick={() => {
                    setIsEditingClass(null);
                    setClassWarnings([]);
                  }} variant="secondary" size="sm" data-id="element-5991">
                            <X className="w-4 h-4" data-id="element-5992" />
                          </Button>
                        </div>
                      </div> : <>
                        <div className="flex items-center gap-4 mb-3 sm:mb-0" data-id="element-5993">
                          <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-black text-lg border border-indigo-100" data-id="element-5994">
                            {classroom.groupLevel}
                          </div>
                          <div data-id="element-5995">
                            <h4 className="font-bold text-slate-900 text-base" data-id="element-5996">
                              {classroom.name}
                            </h4>
                            <div className="flex items-center gap-3 text-sm text-slate-500 font-medium mt-0.5" data-id="element-5997">
                              <span data-id="element-5998">
                                {classroom.learnerIds.length} leerlingen
                              </span>
                              <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-5999" />
                              <div className="flex items-center gap-1.5" data-id="element-6000">
                                <span data-id="element-6001">Code:</span>
                                <span className="font-mono font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded" data-id="element-6002">
                                  {classroom.joinCode}
                                </span>
                                <button onClick={() => handleCopyCode(classroom.joinCode)} className="text-slate-400 hover:text-indigo-600 transition-colors" title="Kopieer code" data-id="element-6003">
                                  {copiedCode === classroom.joinCode ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-6004" /> : <Copy className="w-3.5 h-3.5" data-id="element-6005" />}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-6006">
                          {newClassroomId === classroom.id && <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md mr-2" data-id="element-6007">
                              Nieuw!
                            </span>}
                          <Button onClick={() => {
                    if (isEditingClass !== classroom.id) {
                      setClassWarnings([]);
                    }
                    setIsEditingClass(classroom.id);
                    setEditClassName(classroom.name);
                    setEditGroupLevel(classroom.groupLevel);
                  }} variant="ghost" size="sm" data-id="element-6008">
                            <Edit2 className="w-4 h-4 mr-2" data-id="element-6009" /> Bewerk
                          </Button>
                          <Button onClick={() => setDeleteClassroomId(classroom.id)} variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-id="element-6010">
                            <Trash2 className="w-4 h-4" data-id="element-6011" />
                          </Button>
                        </div>
                      </>}
                  </motion.div>)}
              </div>}
          </div>
        </Card>

        {/* Students Management Section */}
        {classrooms.length > 0 && <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-6012">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-6013">
              <div data-id="element-6014">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-6015">
                  Leerlingen Beheer
                </h3>
                <p className="text-xs text-slate-400 mt-0.5" data-id="element-6016">
                  {activeClassroom ? `${activeClassroom.name} (${classStudents.length} leerlingen)` : `Alle gekoppelde leerlingen (${classStudents.length})`}
                </p>
              </div>
              <Button onClick={() => setShowAddStudent(!showAddStudent)} variant={showAddStudent ? 'secondary' : 'primary'} size="sm" className={showAddStudent ? 'px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2' : 'bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2'} data-id="element-6017">
                {showAddStudent ? <>
                    <X className="w-4 h-4" data-id="element-6018" />
                    Sluiten
                  </> : <>
                    <Plus className="w-4 h-4" data-id="element-6019" />
                    Leerling Toevoegen
                  </>}
              </Button>
            </div>

            <AnimatePresence initial={false} data-id="element-6020">
              {showAddStudent && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="overflow-hidden border-b border-slate-100 bg-slate-50/30" data-id="element-6021">
                  <AddStudentPanelV2 activeAccount={activeAccount} onComplete={() => setShowAddStudent(false)} onCancel={() => setShowAddStudent(false)} classrooms={classrooms} selectedClassroomId={selectedClassroomId} inline={true} data-id="element-6022" />
                </motion.div>}
            </AnimatePresence>

            <div className="p-4" data-id="element-6023">
              {classStudents.length === 0 ? <div className="text-center py-8 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-6024">
                  <div className="mb-3 flex justify-center" data-id="element-6025">
                    <DjayAvatar size="md" emotion="surprised" data-id="element-6026" />
                  </div>
                  <p className="text-slate-500 font-medium" data-id="element-6027">
                    {activeClassroom ? `Nog geen leerlingen in ${activeClassroom.name}.` : 'Je hebt nog geen leerlingen gekoppeld.'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1" data-id="element-6028">
                    {activeClassroom ? 'Voeg leerlingen toe via de knop hierboven.' : 'Selecteer een klas in de zijbalk of voeg leerlingen toe.'}
                  </p>
                </div> : <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-6029">
                  <div className="flex items-center justify-between p-4 bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-6030">
                    <div data-id="element-6031">Leerling</div>
                    <div data-id="element-6032">Acties</div>
                  </div>
                  {classStudents.map((student, i) => {
              const initial = student.name.charAt(0).toUpperCase();
              return <div key={student.id} className={`flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-6033">
                        <div className="flex items-center gap-3" data-id="element-6034">
                          <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm" data-id="element-6035">
                            {initial}
                          </div>
                          <div className="font-bold text-sm text-slate-900" data-id="element-6036">
                            {student.name}
                          </div>
                          {!activeClassroom && (() => {
                    const acc = accounts.accountsById[student.id];
                    if (!acc || !isLearnerAccount(acc) || !acc.grade) return null;
                    return <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs font-bold rounded" data-id="element-6037">
                                  Gr {acc.grade}
                                </span>;
                  })()}
                        </div>
                        <div className="flex items-center gap-2" data-id="element-6038">
                          {activeClassroom ? <>
                              <button onClick={() => {
                      setPendingRemoveStudent({
                        studentId: student.id,
                        studentName: student.name,
                        classroomId: activeClassroom.id,
                        classroomName: activeClassroom.name
                      });
                    }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors group" title="Verwijder uit klas" data-id="element-6039">
                                <UserX className="w-4 h-4" data-id="element-6040" />
                                <span className="hidden sm:inline opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-6041">
                                  Verwijder uit klas
                                </span>
                              </button>
                              <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-6042"></div>
                              <button onClick={() => {
                      setPendingUnlinkStudent({
                        studentId: student.id,
                        studentName: student.name
                      });
                    }} className="p-1.5 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Ontkoppel volledig van jouw account" data-id="element-6043">
                                <UserMinus className="w-4 h-4" data-id="element-6044" />
                              </button>
                            </> : <button onClick={() => {
                    setPendingUnlinkStudent({
                      studentId: student.id,
                      studentName: student.name
                    });
                  }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors group" title="Ontkoppel volledig van jouw account" data-id="element-6045">
                              <UserMinus className="w-4 h-4" data-id="element-6046" />
                              <span className="hidden sm:inline opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-6047">
                                Ontkoppel
                              </span>
                            </button>}
                        </div>
                      </div>;
            })}
                </div>}
            </div>
          </Card>}
      </div>

      <ConfirmDialog open={!!deleteClassroomId} title="Klas verwijderen" message={`Weet je zeker dat je deze klas wilt verwijderen? De leerlingen worden niet verwijderd, alleen de klas wordt opgeheven. Je kunt dit niet ongedaan maken.`} confirmLabel="Ja, verwijder klas" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (deleteClassroomId) {
        removeClassroom(activeAccount.id, deleteClassroomId);
        setDeleteClassroomId(null);
      }
    }} onCancel={() => setDeleteClassroomId(null)} data-id="element-6048" />

      <ConfirmDialog open={!!pendingRemoveStudent} title="Leerling verwijderen uit klas" message={pendingRemoveStudent ? `Weet je zeker dat je ${pendingRemoveStudent.studentName} uit ${pendingRemoveStudent.classroomName} wilt verwijderen? ${pendingRemoveStudent.studentName} blijft wel gekoppeld aan jouw account.` : ''} confirmLabel="Ja, verwijder" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (pendingRemoveStudent) {
        removeLearnerFromClassroom(activeAccount.id, pendingRemoveStudent.classroomId, pendingRemoveStudent.studentId);
        setPendingRemoveStudent(null);
      }
    }} onCancel={() => setPendingRemoveStudent(null)} data-id="element-6049" />

      <ConfirmDialog open={!!pendingUnlinkStudent} title="Koppeling verbreken" message={pendingUnlinkStudent ? `${pendingUnlinkStudent.studentName} wordt verwijderd uit al je klassen en je verliest toegang tot de voortgang. Het leerlingaccount blijft bestaan.` : ''} confirmLabel="Ja, ontkoppel" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (pendingUnlinkStudent) {
        unlinkTeacherFromLearner(activeAccount.id, pendingUnlinkStudent.studentId);
        setPendingUnlinkStudent(null);
      }
    }} onCancel={() => setPendingUnlinkStudent(null)} data-id="element-6050" />
    </PageLayout>;
}